import ScheduleFeed from 'components/Schedule/ScheduleFeed'
import ScheduleImage from 'components/Schedule/ScheduleImage'
import ScheduleList from 'components/Schedule/ScheduleList'
import SectionContent from 'components/Section/SectionContent'
import API from 'core/api'
import React, { useCallback, useMemo } from 'react'
import { useAsync } from 'react-async'
import styled, { css } from 'styled-components'
import { NavLink, useLocation } from 'react-router-dom'
import SkeletonScheduleImage from 'components/Skeleton/SkeletonScheduleImage'
import { getConfig, getMemberId, getPartnerId, getUA } from 'core/singleton'
import SectionTitle from 'components/Section/SectionTitle'
import { IconRear } from 'components/Icons'
import { useRecoilValue } from 'recoil'
import { IS_OPEN_LINK, STORE_MODULE, STORE_ROOT_PROPS } from 'core/store/global'
import { BRIDGE, ONLY_POST_MSG_PARTNERS } from 'core/constants/bridge'
import { CUSTOM_CLASS_NAME, LINK_MOVE_TYPE } from 'core/constants'
import { getExecutorForPartner } from 'core/util/partner'
import { getUrlQueryParameter, moveToLink, parseObjectToQueryString } from 'core/util/url'
import { filterObject } from 'core/util/object'

import { GAHelper } from 'core/ga'
import { GA_EVENT } from 'core/ga/enums'
import { STORE_AIRBRIDGE, STORE_MARKETING_GTM } from 'core/store/analysis'
import useHandleAlarm from 'core/hooks/useHandleAlarm'
import { getLocation, requestBridge } from 'core/util/default'
import { CONFIRM_ALARM } from 'core/store/alarm'
import SectionLayout from 'container/SectionLayout'

interface SectionScheduleProps {
  type?: 'image' | 'feed' | 'list'
}

const SectionSchedule: React.FC<SectionScheduleProps> = (props) => {
  const root = useRecoilValue(STORE_ROOT_PROPS)
  const isOpenLink = useRecoilValue(IS_OPEN_LINK)
  const moduleName = useRecoilValue(STORE_MODULE)
  const airbridge = useRecoilValue(STORE_AIRBRIDGE)
  const marketingGTM = useRecoilValue(STORE_MARKETING_GTM)
  const confirmAlarm = useRecoilValue(CONFIRM_ALARM)
  const { allWording, alarm } = getConfig()
  const { isSDK } = getUA()

  const isBridge = root?.bridge
  const partnerId = getPartnerId()
  const query = getUrlQueryParameter()
  const { isMobile } = getUA()
  const { type } = props
  const location = useLocation()
  const { data, isLoading } = useAsync({
    promiseFn: API.getSchedule,
    limit: 100,
    nextKey: 'init',
  })

  const handleClickSchedule = useCallback(
    ({ item, isConfirmAlarm }: { item: ScheduleItem; isConfirmAlarm: boolean }) =>
      () => {
        if (!item.outputPath) return

        if (marketingGTM?.checkAvailability()) {
          const queryStringPrefix = item.outputPath.indexOf('?') >= 0 ? '&' : '?'
          item.outputPath = item.outputPath.concat(
            `${queryStringPrefix}_gl=${marketingGTM.getGl()}`,
          )
        }

        const utmObject = filterObject(query, (k) => Boolean(k.startsWith('utm')))
        const params: { broadcastUrl?: string; broadcastId?: string; [k: string]: unknown } = {
          broadcastUrl: item.outputPath,
          ...utmObject,
        }
        if (ONLY_POST_MSG_PARTNERS.includes(partnerId)) {
          params.broadcastId = item.broadcastId
        }

        new GAHelper().sendEvent(GA_EVENT.CLICK_CONTENT, {
          partnersId: partnerId,
          location: getLocation(location),
          broadcastTypeCode: item.broadcastTypeCode,
          broadcastName: item.broadcastName,
          position: 'schedule',
        })

        if (getExecutorForPartner(partnerId)?.reqMoveBroadcastBridge?.(item.outputPath)) {
          return
        }
        if (root?.bridge || window[BRIDGE.BROADCAST] || isSDK) {
          requestBridge(BRIDGE.BROADCAST, params)
        } else {
          const queryString = parseObjectToQueryString({
            npc: 'nextlive',
            ...filterObject(query, (k) => Boolean(k.startsWith('utm'))),
          })
          moveToLink({
            airbridge,
            link: `${item.outputPath}${queryString}`,
            type: isOpenLink ? LINK_MOVE_TYPE.REPLACE : LINK_MOVE_TYPE.REDIRECT,
            confirmedAlarmMemberId: isConfirmAlarm ? getMemberId() : '',
          })
        }
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isBridge, isOpenLink, location, marketingGTM, partnerId, query, isSDK],
  )

  /**
   * @name 알림기능
   */
  const { handleClickAlarm } = useHandleAlarm()
  /**
   * @name DOMElement
   **/
  const scheduleWording = useMemo(() => {
    const { scheduleWording } = getConfig()
    return scheduleWording
  }, [])

  const schedule = useMemo(() => {
    if (data) {
      return data.items.map((item) => {
        const {
          thumbnailList,
          broadcastId,
          broadcastName: title,
          shopName,
          programmingStartDt: startTime,
          partnerProfileImageUrl: shopLogo,
          etcLinkUrl,
          etcLinkOpenType,
          usePurchaseEvent: isShowPurchaseAuthBadge,
        } = item

        const isConfirmAlarm = confirmAlarm.some(
          ({ broadcastId: confirmBroadcastId }) => broadcastId === confirmBroadcastId,
        )
        const representThumbnail = thumbnailList.filter(
          (thumbnail) => thumbnail.isRepresentative,
        )[0]
        const useAlarm = alarm?.use || etcLinkOpenType !== 'none'
        const LinkButtonTagAttributes = {
          onClick: handleClickSchedule({ item, isConfirmAlarm }),
        }
        if (type === 'feed') {
          return (
            <ScheduleFeed
              key={broadcastId}
              className={`${CUSTOM_CLASS_NAME.SCHEDULE_CARD}`}
              thumbnail={representThumbnail.thumbnailUrl + '?h=864&w=1152&f=jpg'}
              title={title}
              broadcastId={broadcastId}
              startTime={startTime}
              shopName={shopName}
              shopLogo={shopLogo}
              useAlarm={useAlarm}
              etcLinkUrl={etcLinkUrl}
              etcLinkOpenType={etcLinkOpenType}
              LinkButtonTagAttributes={LinkButtonTagAttributes}
              isShowPurchaseAuthBadge={isShowPurchaseAuthBadge}
              onClickAlarm={() => handleClickAlarm({ broadcastId, ScheduleItem: item })}
              isConfirmAlarm={isConfirmAlarm}
            />
          )
        } else if (type === 'list') {
          return (
            <ScheduleList
              key={broadcastId}
              className={`${CUSTOM_CLASS_NAME.SCHEDULE_CARD}`}
              thumbnail={representThumbnail.thumbnailUrl + '?h=405&w=540&f=jpg'}
              title={title}
              broadcastId={broadcastId}
              startTime={startTime}
              shopName={shopName}
              shopLogo={shopLogo}
              useAlarm={useAlarm}
              etcLinkUrl={etcLinkUrl}
              etcLinkOpenType={etcLinkOpenType}
              LinkButtonTagAttributes={LinkButtonTagAttributes}
              isShowPurchaseAuthBadge={isShowPurchaseAuthBadge}
              onClickAlarm={() => handleClickAlarm({ broadcastId, ScheduleItem: item })}
              isMobile={isMobile}
              isConfirmAlarm={isConfirmAlarm}
            />
          )
        }

        return (
          <ScheduleImage
            key={broadcastId}
            className={`${CUSTOM_CLASS_NAME.SCHEDULE_CARD}`}
            title={title}
            broadcastId={broadcastId}
            thumbnail={representThumbnail.thumbnailUrl + '?h=864&w=1152&f=jpg'}
            useAlarm={useAlarm}
            etcLinkUrl={etcLinkUrl}
            etcLinkOpenType={etcLinkOpenType}
            LinkButtonTagAttributes={LinkButtonTagAttributes}
            isShowPurchaseAuthBadge={isShowPurchaseAuthBadge}
            onClickAlarm={() => handleClickAlarm({ broadcastId, ScheduleItem: item })}
            isConfirmAlarm={isConfirmAlarm}
          />
        )
      }, [])
    }
  }, [data, confirmAlarm, alarm?.use, handleClickSchedule, type, handleClickAlarm, isMobile])

  const skeleton = useMemo(() => {
    if (isLoading) {
      return (
        <>
          <SkeletonScheduleImage />
          <SkeletonScheduleImage />
          <SkeletonScheduleImage />
        </>
      )
    }
  }, [isLoading])

  if (data && data?.count === 0) {
    return <></>
  }

  return (
    <SectionLayout
      elementId="schedule"
      trigger={`${isLoading}`}
      className={`${CUSTOM_CLASS_NAME.SCHEDULE}`}
    >
      <StyledSectionSchedule>
        <SectionTitle className={CUSTOM_CLASS_NAME.SCHEDULE_TITLE}>
          <span>{scheduleWording}</span>
          {!moduleName && (
            <NavLink
              to={`/schedule${location.search}`}
              className="show_all"
              onClick={() => {
                new GAHelper().sendEvent(GA_EVENT.CLICK_MORE, {
                  partnersId: partnerId,
                  location: getLocation(location),
                  position: 'schedule',
                })
              }}
            >
              {allWording === 'IconRear' ? <IconRear /> : allWording}
            </NavLink>
          )}
        </SectionTitle>
        <SectionContent
          className={`section_schedule ${type} ${CUSTOM_CLASS_NAME.SCHEDULE_CONTENTS}`}
        >
          {/* 스켈레톤 */}
          {skeleton}
          {/* 스케쥴 */}
          {schedule}
        </SectionContent>
      </StyledSectionSchedule>
    </SectionLayout>
  )
}

const StyledSectionSchedule = styled.div`
  width: 100%;
  position: relative;

  .show_all {
    color: ${(props) => props.theme.all};
    background: none;
    cursor: pointer;
    border: none;
    margin-left: auto;
    font-size: 14px;
    font-weight: 700;
    font-stretch: normal;
    font-style: normal;
    display: none;
    ${(props) =>
      props.theme.scheduleAll &&
      css`
        display: block;
      `}
  }
`
export default SectionSchedule
